/* eslint-disable no-bitwise -- Writing a binary container format. CRC32 and
   MS-DOS packed date/time are defined in terms of bit operations; expressing
   them with arithmetic would be slower to read and easier to get wrong. */
import {
  SHORTCUTS_FOLDER_NAME,
  type ShortcutManifestEntry,
} from './build-shortcut-manifest';

/**
 * Packs a shortcut manifest into a zip the user downloads — the path for
 * browsers without the File System Access API, and for machines where
 * corporate policy has disabled it.
 *
 * Written by hand rather than pulling in JSZip. Shortcut files are ~100 bytes
 * of text each and a few dozen per archive, so compression buys nothing and a
 * ~100KB dependency to shift a few kilobytes is the wrong trade. Everything
 * here is stored uncompressed (method 0), which is the simplest conformant zip
 * there is: no deflate, no streaming, no worker.
 *
 * Unlike the directory-picker path this is fully verifiable — the output is a
 * byte array any zip tool can check.
 */

const LOCAL_HEADER_SIGNATURE = 0x04_03_4b_50;
const CENTRAL_HEADER_SIGNATURE = 0x02_01_4b_50;
const END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06_05_4b_50;

/** 2.0 — the floor for a stored entry. */
const VERSION = 20;

/** Bit 11: filenames are UTF-8. Ours are user-authored, so this matters. */
const UTF8_FLAG = 0x08_00;

/** Stored, i.e. not compressed. */
const METHOD_STORE = 0;

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) === 1 ? 0xed_b8_83_20 ^ (value >>> 1) : value >>> 1;
    }

    table[index] = value >>> 0;
  }

  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xff_ff_ff_ff;
  for (const byte of bytes) {
    crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xff_ff_ff_ff) >>> 0;
}

/** Zip stores timestamps in the 1980-epoch MS-DOS format, at 2-second resolution. */
function toDosDateTime(date: Date): {time: number; date: number} {
  const year = Math.max(date.getFullYear(), 1980);
  return {
    time:
      (date.getHours() << 11) |
      (date.getMinutes() << 5) |
      (Math.floor(date.getSeconds() / 2) & 0x1f),
    date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
}

type StagedEntry = {
  nameBytes: Uint8Array;
  dataBytes: Uint8Array;
  crc: number;
  offset: number;
};

/**
 * Archive-relative path for an entry, always under a single root folder so
 * unpacking produces one tidy folder instead of loose files on a Desktop.
 * Zip has no need for explicit directory records — tools create intermediate
 * folders from the slashes in the name.
 */
function archivePath(entry: ShortcutManifestEntry): string {
  return [SHORTCUTS_FOLDER_NAME, ...entry.dir, entry.filename].join('/');
}

export function buildShortcutZip(
  entries: ShortcutManifestEntry[],
  modifiedAt: Date = new Date()
): Blob {
  const encoder = new TextEncoder();
  const {time, date} = toDosDateTime(modifiedAt);
  const parts: Uint8Array[] = [];
  const staged: StagedEntry[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = encoder.encode(archivePath(entry));
    const dataBytes = encoder.encode(entry.contents);
    const crc = crc32(dataBytes);

    const header = new Uint8Array(30 + nameBytes.length);
    const view = new DataView(header.buffer);
    view.setUint32(0, LOCAL_HEADER_SIGNATURE, true);
    view.setUint16(4, VERSION, true);
    view.setUint16(6, UTF8_FLAG, true);
    view.setUint16(8, METHOD_STORE, true);
    view.setUint16(10, time, true);
    view.setUint16(12, date, true);
    view.setUint32(14, crc, true);
    view.setUint32(18, dataBytes.length, true);
    view.setUint32(22, dataBytes.length, true);
    view.setUint16(26, nameBytes.length, true);
    view.setUint16(28, 0, true);
    header.set(nameBytes, 30);

    parts.push(header, dataBytes);
    staged.push({nameBytes, dataBytes, crc, offset});
    offset += header.length + dataBytes.length;
  }

  const centralDirectoryOffset = offset;
  let centralDirectorySize = 0;

  for (const entry of staged) {
    const record = new Uint8Array(46 + entry.nameBytes.length);
    const view = new DataView(record.buffer);
    view.setUint32(0, CENTRAL_HEADER_SIGNATURE, true);
    view.setUint16(4, VERSION, true);
    view.setUint16(6, VERSION, true);
    view.setUint16(8, UTF8_FLAG, true);
    view.setUint16(10, METHOD_STORE, true);
    view.setUint16(12, time, true);
    view.setUint16(14, date, true);
    view.setUint32(16, entry.crc, true);
    view.setUint32(20, entry.dataBytes.length, true);
    view.setUint32(24, entry.dataBytes.length, true);
    view.setUint16(28, entry.nameBytes.length, true);
    view.setUint16(30, 0, true);
    view.setUint16(32, 0, true);
    view.setUint16(34, 0, true);
    view.setUint16(36, 0, true);
    view.setUint32(38, 0, true);
    view.setUint32(42, entry.offset, true);
    record.set(entry.nameBytes, 46);

    parts.push(record);
    centralDirectorySize += record.length;
  }

  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, END_OF_CENTRAL_DIRECTORY_SIGNATURE, true);
  endView.setUint16(4, 0, true);
  endView.setUint16(6, 0, true);
  endView.setUint16(8, staged.length, true);
  endView.setUint16(10, staged.length, true);
  endView.setUint32(12, centralDirectorySize, true);
  endView.setUint32(16, centralDirectoryOffset, true);
  endView.setUint16(20, 0, true);
  parts.push(end);

  return new Blob(parts as BlobPart[], {type: 'application/zip'});
}

/**
 * Re-exported for existing importers (`useDesktopShortcuts.ts`) — the actual
 * implementation now lives at `@/matic/lib/utils/download`, consolidated out
 * of what used to be seven independent copies of the same synthetic-anchor
 * download idiom across this codebase (this was one of them).
 */
export {downloadBlob} from '@/matic/lib/utils/download';
